import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card } from 'antd'
import dayjs from 'dayjs'
import {
  dateFormatYearMonthDay,
  downLoadFileBlob,
  formatEndTime,
  formatMessageNew,
  formatStartTime,
  initValueAndLabel,
  uniqueFun
} from '@/utils/common'
import { getReworkSummaryData, getReworkSummaryDataDownload } from '@/services/rfid/reports/rfidReworkDetail'
import SearchBar from '@/components/searchbar/SearchBar'
import AntdTable from '@/components/table/AntdTable'
import { BUSINESS_TYPE_REWORK, CARTON_PE } from '@/utils/types'
import ActionBar from '../../../components/table/ActionBar'
import { modalError } from '../../../utils/modalUtils'
import { paginationInit } from '../../../utils/common'
import { useSelector } from 'react-redux'
import { getOutboundScanDropList } from '../../../services/rfid/reports/rfidTunnelScanAccuracyReport'

const defaultBusinessType = '1'
const startMonth = dayjs().subtract(1, 'day')
const endMonth = dayjs().subtract(1, 'day')
const RfidReworkDetail = () => {
  const [filtersState] = useState({})
  const [tableList, setTableList] = useState([])
  // store the complete dataset returned by backend, paginate on frontend
  const [allData, setAllData] = useState([])
  const [queryFlag, setQueryFlag] = useState(false)
  const [grDate, setGrDate] = useState([startMonth, endMonth])
  const [reworkDate, setReworkDate] = useState([startMonth, startMonth])
  const [businessType, setBusinessType] = useState(defaultBusinessType)
  const mounting = useRef(false)
  const [pagination, setPagination] = useState(paginationInit)
  const language = useSelector(state => state.commonStore.language)
  const getGrDateLabel = useCallback(() => {
    if (businessType === '1') {
      return language === 'zh_CN' ? 'GR 日期' : 'GR Date'
    } else if (businessType === '2') {
      return language === 'zh_CN' ? 'GI 日期' : 'GI Date'
    }
    return formatMessageNew('languageObj.rfidReworkSummary.grDate')
  }, [businessType, language])

  let formRef = useRef(null)
  let searchListInit = [
    {
      type: 'select',
      label: 'languageObj.rfidReworkSummary.businessType',
      key: 'businessType',
      initialValues: defaultBusinessType,
      selectList: BUSINESS_TYPE_REWORK,
      handleChange: (e, idx, item) => {
        setBusinessType(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      },
      isdefaultfilter: 'true'
    },
    {
      type: 'timeRange',
      label: '', // 占位，后续动态设置
      key: 'grDate',
      format: dateFormatYearMonthDay,
      initialValues: [startMonth, endMonth],
      handleChange: (e, idx, item) => {
        setGrDate(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      },
      allowClear: true,
      isdefaultfilter: 'true'
    },
    {
      type: 'timeRange',
      label: 'languageObj.rfidReworkSummary.reworkDate',
      key: 'reworkDate',
      format: dateFormatYearMonthDay,
      initialValues: [startMonth, endMonth],
      handleChange: (e, idx, item) => {
        setReworkDate(e)
      },
      spans: {
        xl: 8,
        xxl: 8
      },
      allowClear: true,
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.rfidReworkDetail.plant',
      key: 'plant',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans: {
        xl: 8,
        xxl: 8
      },
      isdefaultfilter: 'true'
    },
    {
      type: 'select',
      label: 'languageObj.rfidReworkDetail.cartonPe',
      key: 'cartonPe',
      allowClear: true,
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: CARTON_PE,
      spans: {
        xl: 8,
        xxl: 8
      },
      isdefaultfilter: 'true'
    },
    {
      type: 'input',
      label: 'languageObj.rfidReworkDetail.asn',
      key: 'asn',
      initialValues: '',
      spans: {
        xl: 8,
        xxl: 8
      }
    },
    {
      type: 'input',
      label: 'languageObj.rfidReworkDetail.cartonNo',
      key: 'cartonNo',
      initialValues: '',
      spans: {
        xl: 8,
        xxl: 8
      }
    }
  ]
  const [searchList, setSearchList] = useState(() => {
    // 初始化时设置 grDate 的 label
    const list = [...searchListInit]
    list[1].label = getGrDateLabel()
    return list
  })

  // 监听 businessType，动态更新 grDate 的 label
  useEffect(() => {
    setSearchList(prev => {
      const newList = [...prev]
      newList.forEach(item => {
        if (item.key === 'grDate') {
          item.label = getGrDateLabel()
        }
      })
      return newList
    })
  }, [businessType, getGrDateLabel, language])

  const handleTableChange = pagina => {
    // Frontend pagination only: update pagination and slice local data
    const params = {
      ...pagination,
      currentPage: pagina.current,
      pageSize: pagina.pageSize
    }
    setPagination({ ...params })
    // slice data locally
    const start = (params.currentPage - 1) * params.pageSize
    const end = start + params.pageSize
    setTableList(Array.isArray(allData) ? allData.slice(start, end) : [])
  }

  const tableColumns = [
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.plant'),
      dataIndex: 'plant',
      width: 80,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.asnNumber'),
      dataIndex: 'asnNumber',
      width: 80,
      fixed: 'left'
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.po'),
      dataIndex: 'po',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.poItem'),
      dataIndex: 'poItemNumber',
      width: 80
    },
    {
      title:
        businessType === '1'
          ? 'GR ' + formatMessageNew('languageObj.rfidReworkDetail.grDate')
          : 'GI ' + formatMessageNew('languageObj.rfidReworkDetail.grDate'),
      dataIndex: 'grGiDate',
      width: 90
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.reworkDate'),
      dataIndex: 'reworkDate',
      width: 90
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.cartonNo'),
      dataIndex: 'cartonNo',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.cartonPe'),
      dataIndex: 'cartonPe',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.sku'),
      dataIndex: 'skuColorSize',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.upc'),
      dataIndex: 'upc',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.skuColor'),
      dataIndex: 'skuColor',
      width: 90
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.asnQty'),
      dataIndex: 'asnQty',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.reworkType'),
      dataIndex: 'reworkType',
      width: 90
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.reworkLt'),
      dataIndex: 'reworkLt',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.relabelUnit'),
      dataIndex: 'relabelQtyUnit',
      width: 120
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.noRfidTagQtyUnit'),
      dataIndex: 'noRfidTagQtyUnit',
      width: 150
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.scanNoResponseQtyUnit'),
      dataIndex: 'scanNoResponseQtyUnit',
      width: 190
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.multipleRfidCartonQtyUnit'),
      dataIndex: 'multipleRfidCartonQtyUnit',
      width: 205
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.physicalSkuCorrectQtyUnit'),
      dataIndex: 'physicalSkuCorrectQtyUnit',
      width: 205
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.physicalSkuIncorrectQtyUnit'),
      dataIndex: 'physicalSkuIncorrectQtyUnit',
      width: 205
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.productName'),
      dataIndex: 'productName',
      width: 100
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.singleMultipleUpc'),
      dataIndex: 'singleMultipleUpc',
      width: 140
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.factory'),
      dataIndex: 'factory',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.lpnSize'),
      dataIndex: 'lpnSize',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.nfcSku'),
      dataIndex: 'nfcSku',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.wrongPhysicalProduct'),
      dataIndex: 'wrongPhysicalProduct',
      width: 160
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.wrongEpc'),
      dataIndex: 'wrongEpc',
      width: 80
    },
    {
      title: () => formatMessageNew('languageObj.rfidReworkDetail.multipleEpc'),
      dataIndex: 'multipleEpc',
      width: 100
    }
  ]
  useEffect(() => {
    mounting.current && searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])
  useEffect(() => {
    findPlantModulesFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const findPlantModulesFun = () => {
    getOutboundScanDropList().then(res => {
      searchListInit.forEach(itemFirst => {
        if (itemFirst.key === 'plant') {
          const arr = (res.resultData.plants || ['1025', '1027']).filter(item => item === '1025' || item === '1027')
          itemFirst.selectList = initValueAndLabel(uniqueFun(arr, 3))
        }
      })
      setSearchList([...searchListInit])
    })
  }

  const downLoadFun = () => {
    const params = {
      ...getParams(),
      size: 999999
    }
    // Do not send pagination params to backend for download
    getReworkSummaryData(params)
      .then(res => {
        if (res) {
          getReworkSummaryDataDownload(params).then(res => {
            downLoadFileBlob(res)
          })
        }
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  const searchDataBtn = () => {
    mounting.current = true
    const pg = {
      ...pagination,
      currentPage: 1
    }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }
  const searchClearDataBtn = () => {
    mounting.current = true
    formRef && formRef.current.clearForm()
    const pg = {
      ...pagination,
      currentPage: 1
    }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const getParams = () => {
    const info = (formRef.current && formRef.current.getSearchValue()) || {}
    const { grDate, reworkDate } = info
    const params = {
      ...info,
      grDateFrom: formatStartTime(grDate),
      grDateTo: formatEndTime(grDate),
      reworkDateFrom: formatStartTime(reworkDate),
      reworkDateTo: formatEndTime(reworkDate),
      isChinese: language === 'zh_CN' ? true : false
    }

    // 如果 plant 未选择，则使用 searchList 中 plant 的所有选项值作为查询
    const plantItem = (searchList || []).find(item => item.key === 'plant')
    const allPlants = plantItem && Array.isArray(plantItem.selectList) ? plantItem.selectList.map(v => v.value) : []

    params.plant = info.plant && Array.isArray(info.plant) && info.plant.length > 0 ? info.plant : allPlants

    delete params.grDate
    delete params.reworkDate
    return params
  }
  const searchDataFun = () => {
    const params = {
      ...getParams()
      // no size/pageNo, we will paginate on frontend
    }
    // Call backend to get full dataset, then paginate locally
    getReworkSummaryData(params)
      .then(res => {
        if (res && res.resultData) {
          const result = res.resultData
          // normalize different possible shapes
          let dataArray = []
          if (Array.isArray(result)) {
            dataArray = result
          } else if (Array.isArray(result.list)) {
            dataArray = result.list
          } else if (Array.isArray(result.data)) {
            dataArray = result.data
          } else if (Array.isArray(result.rows)) {
            dataArray = result.rows
          } else if (Array.isArray(result.items)) {
            dataArray = result.items
          } else if (result && typeof result === 'object' && result.total && Array.isArray(result.result)) {
            dataArray = result.result
          } else {
            // fallback: if result is an object but not an array, try to use it as single record
            dataArray = Array.isArray(result) ? result : []
          }
          setAllData(dataArray)
          // set pagination total to full data length
          const pg = {
            ...pagination,
            total: dataArray.length
          }
          setPagination(pg)
          // slice current page
          const start = (pg.currentPage - 1) * pg.pageSize
          const end = start + pg.pageSize
          setTableList(dataArray.slice(start, end))
        }
      })
      .catch(err => {
        modalError({
          content: err.errorMessage
        })
      })
  }

  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar
            ref={formRef}
            searchList={searchList}
            showSearchBtn={true}
            showClearBtn={true}
            showCollapseBtn={true}
            onSearch={searchDataBtn}
            onClear={searchClearDataBtn}
          />
        </div>
        <div className="content-container">
          <ActionBar onDownload={downLoadFun} style={{ alignItems: 'end' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 20 }}>
              {/*<Popover*/}
              {/*  trigger="click"*/}
              {/*  color="#f5f5f5"*/}
              {/*  className="cursorPointer"*/}
              {/*  overlayStyle={{*/}
              {/*    maxWidth: '50vw',*/}
              {/*    maxHeight: '95vh',*/}
              {/*    overflow: 'auto',*/}
              {/*    borderRadius: 8*/}
              {/*  }}*/}
              {/*  placement="right"*/}
              {/*  content={getPopoverContent()}*/}
              {/*>*/}
              {/*  <InfoCircleOutlined style={{ fontSize: 18 * deviceSize, marginBottom: 10 * deviceSize }} />*/}
              {/*</Popover>*/}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500
                  }}
                >
                  {(businessType === '1' ? 'GR ' : 'GI ') +
                    formatMessageNew('languageObj.rfidReworkSummary.grDate') +
                    ': ' +
                    dayjs(formatStartTime(grDate)).format('YYYY-MM-DD') +
                    ' to ' +
                    dayjs(formatEndTime(grDate)).format('YYYY-MM-DD')}
                </span>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 500
                  }}
                >
                  {formatMessageNew('languageObj.rfidReworkDetail.reworkDate') +
                    ': ' +
                    dayjs(formatStartTime(reworkDate)).format('YYYY-MM-DD') +
                    ' to ' +
                    dayjs(formatEndTime(reworkDate)).format('YYYY-MM-DD')}
                </span>
              </div>
            </div>
          </ActionBar>
          <AntdTable
            // key={tableKey}
            dataSource={tableList}
            columns={tableColumns}
            // 独一无二的表示，不能重复
            columnsKey="rfidReworkDetailReportColumns"
            pagination={pagination}
            onChange={handleTableChange}
            filters={filtersState} // 绑定 filtersState
          />
        </div>
      </Card>
    </div>
  )
}
export default RfidReworkDetail
